import { memo } from 'react';
import { TestQuestion, TestQuestionOption } from '@features/tests/types/test-detail.types';
import QuestionEditor from '@features/tests/components/QuestionEditor';
import { QuestionBankQuestion, QuestionBankQuestionOption } from '../types/question-bank.types';

interface QuestionBankQuestionAdapterProps {
  question: QuestionBankQuestion;
  questionIndex: number;
  onUpdate: (question: QuestionBankQuestion) => void;
  onRemove: () => void;
  userId: string;
  isExpanded?: boolean;
  isIndividuallyExpanded?: boolean;
  onToggleExpansion?: (questionId: string, isExpanded: boolean) => void;
}

/**
 * Adapter component that allows using the existing QuestionEditor component
 * with QuestionBankQuestion objects
 */
function QuestionBankQuestionAdapter({
  question,
  questionIndex,
  onUpdate,
  onRemove,
  userId,
  isExpanded,
  isIndividuallyExpanded,
  onToggleExpansion,
}: QuestionBankQuestionAdapterProps) {
  // Convert options from Record<string, string> or QuestionBankQuestionOption[] to TestQuestionOption[]
  const convertOptions = (): TestQuestionOption[] => {
    if (Array.isArray(question.options)) {
      return question.options.map(opt => ({
        id: opt.option, // Use option as id
        option: opt.option,
        text: opt.text
      }));
    } else {
      // Convert from Record<string, string>
      return Object.entries(question.options).map(([key, value]) => ({
        id: key,
        option: key,
        text: value
      }));
    }
  };

  // Convert QuestionBankQuestion to TestQuestion format
  const adaptedQuestion: TestQuestion = {
    id: question.id,
    text: question.text,
    options: convertOptions(),
    correctAnswer: question.correctAnswer,
    // Map lineReference to lineReferences (plural) expected by TestQuestion
    lineReferences: question.lineReference ? {
      start: question.lineReference.start,
      end: question.lineReference.end
    } : { start: 0, end: 0 },
    // Add required properties from TestQuestion that don't exist in QuestionBankQuestion
    number: questionIndex + 1, // Use index + 1 as question number
  };

  // Handle updates from the QuestionEditor and convert back to QuestionBankQuestion
  const handleUpdate = (updatedTestQuestion: TestQuestion) => {
    // Convert TestQuestionOption[] back to the original format
    let updatedOptions: Record<string, string> | QuestionBankQuestionOption[];
    
    if (Array.isArray(question.options)) {
      // Convert to QuestionBankQuestionOption[]
      updatedOptions = updatedTestQuestion.options.map(opt => ({
        option: opt.option,
        text: opt.text
      }));
    } else {
      // Convert to Record<string, string>
      updatedOptions = {};
      updatedTestQuestion.options.forEach(opt => {
        (updatedOptions as Record<string, string>)[opt.option] = opt.text;
      });
    }

    const updatedQuestionBankQuestion: QuestionBankQuestion = {
      ...question,
      text: updatedTestQuestion.text,
      options: updatedOptions,
      correctAnswer: updatedTestQuestion.correctAnswer || '',
      lineReference: {
        start: updatedTestQuestion.lineReferences?.start || 0,
        end: updatedTestQuestion.lineReferences?.end || 0
      },
    };
    onUpdate(updatedQuestionBankQuestion);
  };

  return (
    <QuestionEditor
      question={adaptedQuestion}
      questionIndex={questionIndex}
      onUpdate={handleUpdate}
      onRemove={onRemove}
      userId={userId}
      isExpanded={isExpanded}
      isIndividuallyExpanded={isIndividuallyExpanded}
      onToggleExpansion={onToggleExpansion}
    />
  );
}

// Memoize the component to prevent unnecessary re-renders
export default memo(QuestionBankQuestionAdapter);
